import React, { useState, useEffect } from 'react';
import { config } from '../../../config';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
  Card,
  CardContent,
  Switch,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
  ListSubheader,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Backdrop,
} from '@mui/material';
import { FileText, Eye, HelpCircle, X, Info, CheckCircle, AlertCircle, Settings, Image, ArrowRight, Sparkles } from 'lucide-react';
import PageHeader from '../../PageHeader';
import { useAuth } from '../../../context/AuthContext';
import apiClient from '../../../utils/apiUtils';
import ModelSelector from '../../ModelSelector';
import { useAIModels } from '../../../hooks/useAIModels';
import aiClient from '../../../api/clients/AiClient';
import imageClient from '../../../api/clients/ImageClient';
import WordPressClient from '../../../utils/WordPressClient';
import { formatProductReview } from '../../../utils/formatProductReviews';
import ColorPickerDialog from '../../../utils/ColorPickerDialog';
import ImageControls from '../../ImageControls';
import { alpha } from '@mui/material/styles';

const PRIMARY_COLOR = '#2563EB'; // Modern blue
const SECONDARY_COLOR = '#4F46E5'; // Indigo
const SUCCESS_COLOR = '#059669'; // Emerald
const ERROR_COLOR = '#DC2626'; // Red
const WARNING_COLOR = '#D97706'; // Amber
const INFO_COLOR = '#0284C7'; // Sky blue
const BACKGROUND_COLOR = '#F8FAFC'; // Slate 50
const CARD_BACKGROUND = '#FFFFFF';
const BORDER_COLOR = '#E2E8F0'; // Slate 200
const TEXT_PRIMARY = '#1E293B'; // Slate 800
const TEXT_SECONDARY = '#64748B'; // Slate 500

const globalStyles = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const LoadingOverlay = ({ visible, status, steps }) => {
  if (!visible) return null;

  return (
    <Backdrop
      open={visible}
      sx={{
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box
        sx={{
          backgroundColor: CARD_BACKGROUND,
          borderRadius: '16px',
          padding: '32px',
          width: '90%',
          maxWidth: '600px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: '1px solid',
          borderColor: BORDER_COLOR,
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          mb: 4,
          gap: 3
        }}>
          <Box sx={{ 
            width: 80,
            height: 80,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #E04C16 0%, #8B5CF6 50%, #3B82F6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(224, 76, 22, 0.3)',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' },
              '100%': { transform: 'scale(1)' }
            }
          }}>
            <Sparkles 
              size={40} 
              color="#FFFFFF"
              strokeWidth={1.5}
            />
          </Box>
          <Typography variant="h6" sx={{ 
            color: TEXT_PRIMARY, 
            fontWeight: 600,
            fontSize: '1.25rem',
            textAlign: 'center'
          }}>
            {status}
          </Typography>
        </Box>
        
        <Box sx={{ mt: 3 }}>
          {steps.map((step, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2.5,
                opacity: step.completed ? 1 : 0.5,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: step.completed ? SUCCESS_COLOR : 'transparent',
                  border: `2px solid ${step.completed ? SUCCESS_COLOR : TEXT_SECONDARY}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}
              >
                {step.completed && (
                  <CheckCircle size={16} color="white" />
                )}
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: step.completed ? TEXT_PRIMARY : TEXT_SECONDARY,
                  fontWeight: step.completed ? 600 : 400,
                  fontSize: '1rem',
                }}
              >
                {step.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Backdrop>
  );
};

const HelpDialog = ({ open, onClose }) => {
  return (
    <Dialog 
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}>
        <Box sx={{ 
          width: 40, 
          height: 40, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '10px',
          background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
        }}>
          <HelpCircle size={24} />
        </Box>
        <Typography variant="h6">How to Use Product Reviews</Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: 'text.secondary',
        }}
      >
        <X size={20} />
      </IconButton>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            Follow these simple steps to create a professional product review with affiliate links, product comparisons, and engaging content.
          </Typography>
          
          {/* Step 1 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ 
                width: 28, 
                height: 28, 
                borderRadius: '8px',
                background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                1
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                Set Up Your Review
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 3, mt: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Enter a Review Title:</strong> Choose a clear, descriptive title that tells readers what products you're reviewing
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Select Your WordPress Site:</strong> Choose where you want to publish your review
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                <strong>Pick a Category:</strong> Select the most relevant category for your review
              </Typography>
            </Box>
          </Box>

          {/* Step 2 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ 
                width: 28, 
                height: 28, 
                borderRadius: '8px',
                background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                2
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                Add Products to Review
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 3, mt: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Option 1 - Search Mode:</strong> Enter keywords to find products on Amazon (e.g., "best gaming chairs under $200")
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Option 2 - ASIN Mode:</strong> Enter Amazon product ASINs directly (one per line)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                <strong>Tip:</strong> You can review up to 10 products at once
              </Typography>
            </Box>
          </Box>

          {/* Step 3 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ 
                width: 28, 
                height: 28, 
                borderRadius: '8px',
                background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                3
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                Customize Your Review
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 3, mt: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Product Display:</strong> Choose whether to include product boxes with images and details
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Pros & Cons:</strong> Add a comparison table to help readers make decisions
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Buying Guide:</strong> Include helpful tips for choosing the right product
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                <strong>Affiliate Links:</strong> Enable affiliate tracking for your product links
              </Typography>
            </Box>
          </Box>

          {/* Step 4 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ 
                width: 28, 
                height: 28, 
                borderRadius: '8px',
                background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                4
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                Add Images (Optional)
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 3, mt: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Featured Image:</strong> Generate a custom image for your review
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                <strong>Product Images:</strong> Include product photos in your review
              </Typography>
            </Box>
          </Box>

          {/* Step 5 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ 
                width: 28, 
                height: 28, 
                borderRadius: '8px',
                background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                5
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                Generate and Publish
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 3, mt: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Click "Generate Product Review":</strong> Our AI will create a comprehensive review
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                <strong>Review and Publish:</strong> Check the generated content and publish to your WordPress site
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            mt: 2, 
            p: 2, 
            borderRadius: '8px', 
            backgroundColor: 'rgba(224, 76, 22, 0.05)',
            border: '1px solid rgba(224, 76, 22, 0.1)'
          }}>
            <Typography variant="body2" sx={{ color: '#E04C16', fontWeight: 500 }}>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 4, 
            py: 1.2,
            background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
            boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #CC4414, #E85E35)',
            }
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function ProductReviewsPage({onGeneratingChange}) {
  // Auth and API hooks
  const { authenticatedRequest, error: authError, loading: authLoading } = useAuth();
  const { models: availableModels, loading: modelsLoading, error: modelsError } = useAIModels(authenticatedRequest);

  // Dialog and navigation state
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState(null);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generationStatus, setGenerationStatus] = useState('');
  const [generationSteps, setGenerationSteps] = useState([
    { id: 'init', text: 'Initializing content generation', status: 'pending', icon: <Settings size={16} /> },
    { id: 'content', text: 'Generating article content', status: 'pending', icon: <FileText size={16} /> },
    { id: 'images', text: 'Processing images', status: 'pending', icon: <Image size={16} /> },
    { id: 'upload', text: 'Publishing to WordPress', status: 'pending', icon: <ArrowRight size={16} /> }
  ]);

  // WordPress site settings
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // Content settings
  const [title, setTitle] = useState('');
  const [model, setModel] = useState('');
  const [prompts, setPrompts] = useState({ systemTemplates: [], userPrompts: [] });
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState(null);

  // Product search and selection
  const [searchMode, setSearchMode] = useState('asin');
  const [searchQuery, setSearchQuery] = useState('');
  const [productCount, setProductCount] = useState(5);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('us-west-1');
  const [productAsins, setProductAsins] = useState('');
  const [validatedProducts, setValidatedProducts] = useState([]);

  // Product display settings
  const [includeProductBoxes, setIncludeProductBoxes] = useState(true);
  const [includeProsConsSection, setIncludeProsConsSection] = useState(true);
  const [includeBuyingGuide, setIncludeBuyingGuide] = useState(false);
  const [buyingGuidePosition, setBuyingGuidePosition] = useState('bottom');
  const [linkHeadings, setLinkHeadings] = useState(true);
  const [includeAffiCarousel, setIncludeAffiCarousel] = useState(false);
  const [affiCarouselPosition, setAffiCarouselPosition] = useState('top');

  // Disclaimer settings
  const [includeDisclaimer, setIncludeDisclaimer] = useState(true);
  const [disclaimerText, setDisclaimerText] = useState("As an Amazon Associate I earn from qualifying purchases.");
  const [disclaimerPosition, setDisclaimerPosition] = useState('top');

  // Button settings
  const [includeCheckButton, setIncludeCheckButton] = useState(true);
  const [buttonColor, setButtonColor] = useState('#FF9900');
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Image settings
  const [productImageType, setProductImageType] = useState('none');
  const [availableImageModels, setAvailableImageModels] = useState([]);
  const [includeFeaturedImage, setIncludeFeaturedImage] = useState(false);
  const [featuredImageModel, setFeaturedImageModel] = useState('fal-ai/flux/schnell');
  const [featuredImageStyle, setFeaturedImageStyle] = useState('realistic_image');
  const [featuredDimension, setFeaturedDimension] = useState('square');
  const [featuredImagePrompt, setFeaturedImagePrompt] = useState('');
  const [useAIEnhancement, setUseAIEnhancement] = useState(false);

  // AFFI widget settings
  const [useAffiShortcodes, setUseAffiShortcodes] = useState(true);
  const [customAffiShortcode, setCustomAffiShortcode] = useState('');
  const [affiShortcodePlacement, setAffiShortcodePlacement] = useState('after-title');
  
 
  const AMAZON_MARKETPLACES = [
    { 
      value: 'com', 
      label: 'Amazon.com (US)', 
      domain: 'https://amazon.com',
      region: 'us-west-1'
    },
    { 
      value: 'co.uk', 
      label: 'Amazon.co.uk (UK)', 
      domain: 'https://amazon.co.uk',
      region: 'eu-west-1'
    },
    { 
      value: 'ca', 
      label: 'Amazon.ca (Canada)', 
      domain: 'https://amazon.ca',
      region: 'ca-central-1'
    },
    { 
      value: 'de', 
      label: 'Amazon.de (Germany)', 
      domain: 'https://amazon.de',
      region: 'eu-central-1'
    }
  ];


  const [amazonMarketplace, setAmazonMarketplace] = useState(AMAZON_MARKETPLACES[0]);
  const [amazonDomain, setAmazonDomain] = useState('com');
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Computed values
  const hasProducts = searchMode === 'search' 
    ? selectedProducts.length > 0 
    : productAsins.trim().length > 0;

  // Constants
  const DEFAULT_AFFI_SHORTCODE = '[affiai asin="{asin}" template="box"]';

 
useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (isGenerating) {
      e.preventDefault();
      setNavDialogOpen(true);
      e.returnValue = '';
      return '';
    }
  };

  const handleUnload = (e) => {
    if (isGenerating) {
      e.preventDefault();
      setNavDialogOpen(true);
      return false;
    }
  };

  const handlePopState = (event) => {
    if (isGenerating) {
      event.preventDefault();
      window.history.pushState(null, '', window.location.pathname);
      setNavDialogOpen(true);
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('unload', handleUnload);
  window.addEventListener('popstate', handlePopState);
  
  if (isGenerating) {
    window.history.pushState(null, '', window.location.pathname);
  }

  onGeneratingChange?.(isGenerating);
  
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('unload', handleUnload);
    window.removeEventListener('popstate', handlePopState);
    onGeneratingChange?.(false);
  };
}, [isGenerating, onGeneratingChange]);

useEffect(() => {
  const fetchInitialData = async () => {
    try {
      await authenticatedRequest(async (token) => {
        apiClient.setAuthToken(token);
        
        let sitesResponse;
        let imageModelsResponse;
        let promptsResponse;

        try {
          [sitesResponse, imageModelsResponse, promptsResponse] = await Promise.all([
            apiClient.getWordPressSites(),
            apiClient.getAvailableImageModels(),
            apiClient.getSavedPrompts()
          ]);

          setSites(sitesResponse);
          if (sitesResponse.length > 0) {
            setSelectedSite(sitesResponse[0].id);
          }

          setAvailableImageModels(imageModelsResponse);

          if (promptsResponse) {
            // First, gather all product review templates from both system and user prompts
            const systemProductReviews = (promptsResponse.system_templates || []).filter(template => 
              template.type === 'product_review' || 
              template.settings?.type === 'product_review' ||
              template.title?.toLowerCase().includes('product review')
            );

            const userProductReviews = (promptsResponse.user_prompts || []).filter(template =>
              template.type === 'product_review' || 
              template.settings?.type === 'product_review' ||
              template.title?.toLowerCase().includes('product review')
            );

            // Combine both arrays
            const allProductReviews = [...systemProductReviews, ...userProductReviews];

            setPrompts({
              systemTemplates: promptsResponse.system_templates || [],
              userPrompts: promptsResponse.user_prompts || [],
              productReviewTemplates: allProductReviews // Add new property for filtered templates
            });
            
            // Set the first product review template as selected if any exist
            if (allProductReviews.length > 0) {
              setSelectedPrompt(allProductReviews[0].id);
            }
          }

        } catch (error) {
          if (!sitesResponse) console.error('Failed to fetch WordPress sites');
          if (!imageModelsResponse) console.error('Failed to fetch image models');
          if (!promptsResponse) console.error('Failed to fetch prompts');
          throw error;
        }
      });
    } catch (err) {
      setError('Failed to load initial data');
      console.error('Error fetching initial data:', err);
    }
  };

  fetchInitialData();
}, []);

  useEffect(() => {
    console.log('Model initialization check:', {
      availableModels: availableModels.length,
      currentModel: model
    });

    if (availableModels.length > 0 && !model) {
      setModel(availableModels[0].value);
    }
  }, [availableModels, model]);

  useEffect(() => {
    if (selectedSite) {
      fetchCategories();
    }
  }, [selectedSite]);

// Add this temporarily to debug
useEffect(() => {
  console.log('Prompts data:', {
    allPrompts: prompts,
    systemTemplates: prompts.systemTemplates,
    productReviewTemplates: prompts.systemTemplates?.filter(t => t.type === 'product_review')
  });
}, [prompts]);
 
useEffect(() => {
  if (availableModels.length > 0) {
    const initializeAI = async () => {
      try {
        const keys = await authenticatedRequest(() => apiClient.getApiKeys());
        
        // Initialize AI client
        aiClient.setApiKeys(keys);
        aiClient.updateModelConfigs(availableModels); 

        // Initialize image client
        imageClient.setAvailableModels(availableModels);
        imageClient.setAiClient(aiClient);
        imageClient.setApiKeys(keys);
        
        // Set initial model
        if (!model && availableModels.length > 0) {
          const defaultModel = availableModels[0].value;
          setModel(defaultModel);
        }
      } catch (error) {
        console.error('Error initializing AI services:', error);
        setError('Failed to initialize AI services');
      }
    };

    initializeAI();
  }
}, [availableModels, authenticatedRequest, model]);

const LeaveConfirmationDialog = ({ open, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Leave Page?</DialogTitle>
    <DialogContent>
      <Typography>
        Review is being generated. If you leave now, all progress will be lost.
      </Typography>
      <Typography color="error" sx={{ mt: 2 }}>
        Are you sure you want to leave this page?
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>
        Stay on Page
      </Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Leave Page
      </Button>
    </DialogActions>
  </Dialog>
);

const NavigationDialog = () => (
  <Dialog open={navDialogOpen} onClose={() => setNavDialogOpen(false)}>
    <DialogTitle>Leave Page?</DialogTitle>
    <DialogContent>
      <Typography>
        Review is being generated. If you leave now, all progress will be lost.
      </Typography>
      <Typography color="error" sx={{ mt: 2 }}>
        Are you sure you want to leave this page?
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setNavDialogOpen(false)}>
        Stay on Page
      </Button>
      <Button
        onClick={() => {
          setIsGenerating(false);
          setNavDialogOpen(false);
          window.history.back();
        }}
        color="error"
        variant="contained"
      >
        Leave Page
      </Button>
    </DialogActions>
  </Dialog>
);


  const handlePreviewPrompt = (promptId) => {
    const prompt = [
      ...prompts.systemTemplates,
      ...prompts.userPrompts
    ].find(p => p.id === promptId);
    
    if (prompt) {
      setPreviewPrompt(prompt);
      setPreviewOpen(true);
    }
  };
  

  const fetchCategories = async () => {
    try {
      await authenticatedRequest(async (token) => {
        apiClient.setAuthToken(token);
        const categoriesData = await apiClient.getCategories(selectedSite);
        setCategories(categoriesData);
      });
    } catch (err) {
      setError('Failed to load categories');
      console.error('Error fetching categories:', err);
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setError(null);
    const marketplace = AMAZON_MARKETPLACES.find(m => m.value === amazonDomain);

    try {
      await authenticatedRequest(async (token) => {
        apiClient.setAuthToken(token);
        const response = await apiClient.searchProducts({
          query: searchQuery,
          maxResults: productCount,
          region: marketplace.region
        });
        
        setSearchResults(response.products);
        if (response.products.length > 0) {
          setSelectedProducts(response.products);
          setProductAsins(response.products.map(p => p.asin).join('\n'));
        }
      });
    } catch (err) {
      setError(err.message || 'Failed to search products');
    } finally {
      setIsLoading(false);
    }
  };

  const generateProductContent = async (productData, prompt, title) => {
    if (!prompt) throw new Error('Selected prompt template not found');
    if (!model) throw new Error('No AI model selected');
  
    const buyingGuidePrompt = includeBuyingGuide ? `
  Please include a buying guide section with the heading "## Buying Guide". Write it as a friendly, conversational 3-paragraph guide that feels like getting advice from a knowledgeable salesperson. Focus on helping the reader understand what makes a good ${title.toLowerCase()}, what to look out for, and how to make the best choice for their needs. Avoid using subheadings or bullet points - keep it flowing naturally as a conversation. Place it at the ${buyingGuidePosition} of the article.
  ` : '';
  
    const structuredPrompt = `${prompt.prompt_text}
  
  Article Title: ${title}
  
  ${buyingGuidePrompt}
  
  Products to Review:
  ${productData.map((product, index) => `
  ${index + 1}. ${product.title}
  ASIN: ${product.asin}
  Features: ${product.features?.join(', ') || 'Not available'}
  Rating: ${product.rating || 'Not available'}
  Description: ${product.description || 'Not available'}
  Price: ${product.price || 'Not available'}
  `).join('\n')}
  
  Additional Requirements:
  - Present products in a clear, organized manner
  - Include specific features and benefits for each product
  - Compare products where relevant
  - Use proper heading hierarchy (h2 for main sections)
  - Make content engaging and informative`;

  console.log('Generated Prompt:', structuredPrompt);
  
    return await aiClient.generateArticleContent({
      title,
      modelId: model,
      customPrompt: structuredPrompt
    });
  };

  const validateAndGatherProductData = async (asins) => {
    try {
      const response = await authenticatedRequest(async (token) => {
        apiClient.setAuthToken(token);
        return await apiClient.validateAndGatherProducts({ asins });
      });
      return response.products;
    } catch (error) {
      throw new Error(error.message || 'Failed to gather product data');
    }
  };

  const updateStepStatus = (stepId, completed) => {
    setGenerationSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, completed } : step
    ));
  };

  const resetGenerationSteps = () => {
    setGenerationSteps(prev => prev.map(step => ({ ...step, completed: false })));
  };

const handleGenerateReview = async (e) => {
  e.preventDefault();
  setError(null);
  setIsGenerating(true);
  setGenerationStatus('Starting review generation...');

  // Initialize steps
  resetGenerationSteps();

  try {
    const asins = searchMode === 'search' 
      ? selectedProducts.map(p => p.asin)
      : productAsins.split('\n').map(asin => asin.trim()).filter(asin => asin);

    if (asins.length === 0) {
      throw new Error('Please enter at least one product ASIN');
    }

    if (asins.length > 10) {
      throw new Error('Maximum 10 products allowed');
    }

    // Get product data
    setGenerationStatus('Gathering product data...');
    updateStepStatus('init', true);
    const productData = await validateAndGatherProductData(asins);

    // Find selected prompt template
    const prompt = [...prompts.systemTemplates, ...prompts.userPrompts]
      .find(p => p.id === selectedPrompt);
    
    if (!prompt) {
      throw new Error('Please select a writing style template');
    }

    // Generate content
    setGenerationStatus('Generating article content...');
    updateStepStatus('content', true);
    const content = await generateProductContent(productData, prompt, title);
    let processedContent = formatProductReview(content, title, {
      includeDisclaimer,
      disclaimerText,
      disclaimerPosition,
      includeCheckButton,
      useAffiShortcodes,
      affiShortcodeTemplate: customAffiShortcode || DEFAULT_AFFI_SHORTCODE,
      affiShortcodePlacement,
      tracking_id: productData[0]?.tracking_id,
      buttonColor: buttonColor,  
      includeProsConsSection,
      includeBuyingGuide,
      buyingGuidePosition,
      amazonDomain,
      includeAffiCarousel: includeAffiCarousel ? true : false,
      affiCarouselPosition,
      productAsins: asins.join(',')
    });

    // Handle featured image if enabled
    let featuredMediaId = null;
    if (includeFeaturedImage) {
      try {
        setGenerationStatus('Generating featured image...');
        updateStepStatus('images', true);
        const imageData = await imageClient.generateImage({
          prompt: featuredImagePrompt || title,
          model: featuredImageModel,
          dimension: featuredDimension,
          style: featuredImageStyle,
          enhancePrompt: useAIEnhancement,
          context: {
            articleTitle: title
          }
        });

        if (imageData?.image_url) {
          setGenerationStatus('Uploading featured image...');
          const response = await fetch(imageData.image_url);
          const blob = await response.blob();
          const formData = new FormData();
          formData.append('file', blob, `featured-${Date.now()}.png`);
          formData.append('siteId', selectedSite);

          const uploadResult = await authenticatedRequest(() => 
            apiClient.uploadMedia(formData)
          );
          featuredMediaId = uploadResult?.media_id;
        }
      } catch (error) {
        console.error('Featured image error:', error);
        setGenerationStatus('Featured image failed, continuing...');
      }
    }

    // Create WordPress post
    setGenerationStatus('Creating WordPress post...');
    updateStepStatus('upload', true);
    const site = sites.find(s => s.id === selectedSite);
    if (!site) throw new Error('Selected site not found');

    const wpClient = new WordPressClient(site);
    const credentials = await authenticatedRequest(() => 
      apiClient.getWordPressCredentials(selectedSite)
    );
    wpClient.setCredentials(credentials.username, credentials.password);

    const post = await wpClient.createPost({
      title,
      content: processedContent,
      categoryId: selectedCategory,
      featuredMediaId,
      status: 'draft'
    });

    // Update steps to completed
    setGenerationSteps(prev => prev.map(step => 
      step.status === 'in-progress' ? { ...step, status: 'completed' } : step
    ));
    setGenerationStatus('Review generated successfully!');

    setNotification({
      open: true,
      message: 'Product review generated successfully!',
      severity: 'success'
    });

  } catch (error) {
    console.error('Error generating review:', error);
    setError(error.message || 'Failed to generate product review');
    setNotification({
      open: true,
      message: error.message || 'Failed to generate product review',
      severity: 'error'
    });
    // Reset generation steps on error
    resetGenerationSteps();
  } finally {
    setIsGenerating(false);
    setGenerationStatus('');
  }
};
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };
  return (
    <Box sx={{ 
      p: 4,
      background: BACKGROUND_COLOR,
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4 
      }}>
        <PageHeader 
          category="Content Creation"
          title="Create An Amazon Product Roundup Review" 
          description="Generate comprehensive product reviews by selecting products, customizing display settings, and adding optional features like featured images and product widgets."
          icon={
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 10px rgba(37, 99, 235, 0.2)',
            }}>
              <FileText size={24} color="#FFFFFF" strokeWidth={1.5} />
            </Box>
          }
        />
        <Box sx={{ mr: 6 }}>
          <Button
            onClick={() => setHelpDialogOpen(true)}
            startIcon={<HelpCircle size={18} />}
            sx={{
              bgcolor: 'rgba(224, 76, 22, 0.08)',
              color: '#E04C16',
              borderRadius: '12px',
              px: 3,
              py: 1,
              minWidth: '140px',
              fontWeight: 600,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              border: '1px solid rgba(224, 76, 22, 0.12)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'rgba(224, 76, 22, 0.12)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.12)'
              }
            }}
          >
            How to Use
          </Button>
        </Box>
      </Box>

      {/* Main Content - Two Column Layout */}
      <Grid container spacing={3}>
        {/* Left Column - Basic Settings */}
        <Grid item xs={12} md={6}>
          <form onSubmit={(e) => {
            e.preventDefault();
            handleGenerateReview(e);
          }}>
            <Grid container spacing={3}>
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                </Grid>
              )}
              
              {/* Basic Information Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: BORDER_COLOR,
                    background: CARD_BACKGROUND,
                    overflow: 'hidden',
                    mb: 3
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mb: 3,
                    position: 'relative'
                  }}>
                    <Box sx={{ 
                      minWidth: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                      fontSize: '1rem',
                      fontWeight: 700
                    }}>
                      1
                    </Box>
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#1F2937',
                          position: 'relative',
                          display: 'inline-block',
                          fontSize: '1.125rem',
                          lineHeight: 1.2,
                          pb: 0.5
                        }}
                      >
                        Article Details
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 0.5, fontSize: '0.875rem' }}
                      >
                        Enter the basic details for your product review
                      </Typography>
                    </Box>
                  </Box>
                
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Review Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        disabled={isGenerating}
                        helperText="Enter a descriptive title for your product review"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>WordPress Site</InputLabel>
                        <Select
                          value={selectedSite}
                          onChange={(e) => setSelectedSite(e.target.value)}
                          disabled={isGenerating}
                          sx={{
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha('#6B7280', 0.2),
                            },
                          }}
                        >
                          {sites.map(site => (
                            <MenuItem key={site.id} value={site.id}>
                              {site.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Select the WordPress site where the product review will be published
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          disabled={isGenerating}
                          sx={{
                            borderRadius: '8px',
                          }}
                        >
                          {categories.map(category => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Product Selection Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: BORDER_COLOR,
                    background: CARD_BACKGROUND,
                    overflow: 'hidden',
                    mb: 3
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mb: 3,
                    position: 'relative'
                  }}>
                    <Box sx={{ 
                      minWidth: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                      fontSize: '1rem',
                      fontWeight: 700
                    }}>
                      2
                    </Box>
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#1F2937',
                          position: 'relative',
                          display: 'inline-block',
                          fontSize: '1.125rem',
                          lineHeight: 1.2,
                          pb: 0.5
                        }}
                      >
                        Product Selection
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 0.5, fontSize: '0.875rem' }}
                      >
                        Enter one or more Amazon product ASINs to generate a review article. You can find the ASIN in the product URL or below the product details.
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {searchMode === 'search' ? 'Search for Products' : 'Enter Product ASINs'}
                        </Typography>
                        <Tooltip title="Enter Amazon product ASINs manually or search for products to review. You can add up to 10 products.">
                          <IconButton size="small">
                            <HelpCircle size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={searchMode === 'search'}
                            onChange={() => setSearchMode(searchMode === 'search' ? 'asin' : 'search')}
                            disabled={isGenerating}
                            size="small"
                          />
                        }
                        label={searchMode === 'search' ? 'Search Mode' : 'Manual ASIN Mode'}
                      />
                    </Box>
                  </Box>
                  
                  {searchMode === 'search' ? (
                    <Box component="div" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Search Query"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            disabled={isGenerating}
                            placeholder="e.g., best gaming chairs under $200"
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            type="number"
                            label="Number of Products"
                            value={productCount}
                            onChange={(e) => setProductCount(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                            inputProps={{ min: 1, max: 10 }}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={isGenerating || isLoading || !searchQuery}
                            sx={{ 
                              height: '100%',
                              borderRadius: '8px',
                              bgcolor: PRIMARY_COLOR,
                              '&:hover': {
                                bgcolor: alpha(PRIMARY_COLOR, 0.9)
                              }
                            }}
                            fullWidth
                            size="small"
                          >
                            {isLoading ? <CircularProgress size={20} /> : 'Search'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Enter Amazon ASINs (one per line, max 10)"
                      value={productAsins}
                      onChange={(e) => setProductAsins(e.target.value)}
                      helperText="Enter each Amazon ASIN on a new line"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        }
                      }}
                    />
                  )}

                  {/* Display search results */}
                  {searchResults.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500, color: TEXT_PRIMARY }}>
                        Selected Products
                      </Typography>
                      <TableContainer sx={{ 
                        mt: 2,
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: BORDER_COLOR,
                      }}>
                        <Table size="small">
                          <TableBody>
                            {searchResults.map((product) => (
                              <TableRow key={product.asin}>
                                <TableCell sx={{ width: 80, p: 1 }}>
                                  <Box
                                    component="img"
                                    src={product.image_url}
                                    alt={product.title}
                                    sx={{
                                      width: 60,
                                      height: 60,
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ maxWidth: 360 }}>
                                    <Typography 
                                      variant="body2" 
                                      noWrap
                                      component="a"
                                      href={`https://amazon.${amazonDomain}/dp/${product.asin}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        color: PRIMARY_COLOR,
                                        textDecoration: 'none',
                                        '&:hover': {
                                          textDecoration: 'underline'
                                        }
                                      }}
                                    >
                                      {product.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      ASIN: {product.asin}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* AI Configuration Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: BORDER_COLOR,
                    background: CARD_BACKGROUND,
                    overflow: 'hidden',
                    mb: 3
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mb: 3,
                    position: 'relative'
                  }}>
                    <Box sx={{ 
                      minWidth: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                      fontSize: '1rem',
                      fontWeight: 700
                    }}>
                      3
                    </Box>
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#1F2937',
                          position: 'relative',
                          display: 'inline-block',
                          fontSize: '1.125rem',
                          lineHeight: 1.2,
                          pb: 0.5
                        }}
                      >
                        AI Configuration
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 0.5, fontSize: '0.875rem' }}
                      >
                        Configure the AI model and writing style
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <ModelSelector
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        required
                        tooltip="Choose the AI model that will generate your content"
                        label="AI MODEL"
                        disabled={isGenerating || modelsLoading}
                        showDescription={true}
                      />
                      {modelsError && (
                        <FormHelperText error>
                          {modelsError}
                        </FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Writing Style Template</InputLabel>
                        <Select
                          value={selectedPrompt}
                          label="Writing Style Template"
                          disabled={isGenerating || modelsLoading}
                          onChange={(event) => setSelectedPrompt(event.target.value)}
                          sx={{
                            borderRadius: '8px',
                          }}
                        >
                          {[...prompts.systemTemplates, ...prompts.userPrompts]
                            .filter(template => 
                              template.type === 'product_review' || 
                              template.settings?.type === 'product_review' ||
                              template.name?.toLowerCase().includes('product review') ||
                              template.name === '**Product Reviews Extended**'
                            )
                            .map((prompt) => (
                              <MenuItem key={prompt.id} value={prompt.id}>
                                <Box>
                                  <Typography variant="subtitle2">{prompt.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Detailed Amazon Product Roundup Review Prompt
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      {selectedPrompt && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <Button
                            startIcon={<Eye size={16} />}
                            onClick={() => handlePreviewPrompt(selectedPrompt)}
                            size="small"
                            sx={{ color: TEXT_SECONDARY }}
                          >
                            Preview Template
                          </Button>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </form>
        </Grid>

        {/* Right Column - Display and Additional Settings */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            {/* Product Display Settings */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: BORDER_COLOR,
                  background: CARD_BACKGROUND,
                  overflow: 'hidden',
                  mb: 3
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  mb: 3,
                  position: 'relative'
                }}>
                  <Box sx={{ 
                    minWidth: 40,
                    height: 40,
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                    fontSize: '1rem',
                    fontWeight: 700
                  }}>
                    4
                  </Box>
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#1F2937',
                        position: 'relative',
                        display: 'inline-block',
                        fontSize: '1.125rem',
                        lineHeight: 1.2,
                        pb: 0.5
                      }}
                    >
                      Display Settings
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mt: 0.5, fontSize: '0.875rem' }}
                    >
                      Customize how your product review will be displayed
                    </Typography>
                  </Box>
                </Box>
                
                <Grid container spacing={2}>
                  {/* Product Display Options */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: TEXT_PRIMARY }}>
                      Product Display
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeProductBoxes}
                          onChange={(e) => setIncludeProductBoxes(e.target.checked)}
                          disabled={isGenerating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Include Product Boxes
                          <Tooltip title="Adds product information boxes with images, prices, and features for each reviewed product">
                            <IconButton size="small" sx={{ p: 0 }}>
                              <Info size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeAffiCarousel}
                          onChange={(e) => setIncludeAffiCarousel(e.target.checked)}
                          disabled={isGenerating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Include AFFI Carousel
                          <Tooltip title="Adds a scrollable carousel of product images at the top or bottom of the article with affiliate tracking">
                            <IconButton size="small" sx={{ p: 0 }}>
                              <Info size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Grid>
                  {includeAffiCarousel && (
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Carousel Position</InputLabel>
                        <Select
                          value={affiCarouselPosition}
                          onChange={(e) => setAffiCarouselPosition(e.target.value)}
                          disabled={isGenerating}
                          sx={{ borderRadius: '8px' }}
                        >
                          <MenuItem value="top">Top of Article</MenuItem>
                          <MenuItem value="bottom">Bottom of Article</MenuItem>
                        </Select>
                        <FormHelperText>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Choose where to place the product carousel
                            <Tooltip title="Placing the carousel at the top helps readers quickly see all products, while placing it at the bottom can help with conversion after they've read the reviews">
                              <IconButton size="small" sx={{ p: 0 }}>
                                <Info size={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}

                  {/* Content Structure */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: TEXT_PRIMARY }}>
                      Content Structure
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeProsConsSection}
                          onChange={(e) => setIncludeProsConsSection(e.target.checked)}
                          disabled={isGenerating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Include Pros and Cons Section
                          <Tooltip title="Adds a table comparing the pros and cons of each product for easy comparison">
                            <IconButton size="small" sx={{ p: 0 }}>
                              <Info size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={linkHeadings}
                          onChange={(e) => setLinkHeadings(e.target.checked)}
                          disabled={isGenerating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Link Headings
                          <Tooltip title="Makes product headings clickable links to their Amazon product pages">
                            <IconButton size="small" sx={{ p: 0 }}>
                              <Info size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeBuyingGuide}
                          onChange={(e) => setIncludeBuyingGuide(e.target.checked)}
                          disabled={isGenerating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Include Buying Guide
                          <Tooltip title="Adds a comprehensive guide to help readers choose the right product for their needs">
                            <IconButton size="small" sx={{ p: 0 }}>
                              <Info size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Grid>
                  {includeBuyingGuide && (
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Buying Guide Position</InputLabel>
                        <Select
                          value={buyingGuidePosition}
                          onChange={(e) => setBuyingGuidePosition(e.target.value)}
                          disabled={isGenerating}
                          sx={{ borderRadius: '8px' }}
                        >
                          <MenuItem value="top">Top of Article</MenuItem>
                          <MenuItem value="bottom">Bottom of Article</MenuItem>
                        </Select>
                        <FormHelperText>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Choose where to place the buying guide
                            <Tooltip title="Placing the guide at the top helps readers make informed decisions before reading reviews, while placing it at the bottom can help reinforce their choice">
                              <IconButton size="small" sx={{ p: 0 }}>
                                <Info size={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}

                  {/* Affiliate Settings */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: TEXT_PRIMARY }}>
                      Affiliate Settings
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useAffiShortcodes}
                          onChange={(e) => setUseAffiShortcodes(e.target.checked)}
                          disabled={isGenerating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Use AFFI Shortcodes
                          <Tooltip title="Enables affiliate tracking shortcodes for product links and boxes">
                            <IconButton size="small" sx={{ p: 0 }}>
                              <Info size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeCheckButton}
                          onChange={(e) => setIncludeCheckButton(e.target.checked)}
                          disabled={isGenerating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Include Check Button
                          <Tooltip title="Adds a prominent 'Check on Amazon' button below each product review">
                            <IconButton size="small" sx={{ p: 0 }}>
                              <Info size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Grid>
                  {includeCheckButton && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '6px',
                            bgcolor: buttonColor,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: BORDER_COLOR,
                          }}
                          onClick={() => setShowColorPicker(true)}
                        />
                        <Typography variant="body2" color={TEXT_SECONDARY}>
                          Button Color
                        </Typography>
                        <Tooltip title="Choose a color that matches your site's design for the 'Check on Amazon' buttons">
                          <IconButton size="small" sx={{ p: 0 }}>
                            <Info size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  )}

                  {/* Disclaimer */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: TEXT_PRIMARY }}>
                      Disclaimer
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeDisclaimer}
                          onChange={(e) => setIncludeDisclaimer(e.target.checked)}
                          disabled={isGenerating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Include Disclaimer
                          <Tooltip title="Adds an affiliate disclosure statement to comply with FTC guidelines">
                            <IconButton size="small" sx={{ p: 0 }}>
                              <Info size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                    {includeDisclaimer && (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        value={disclaimerText}
                        onChange={(e) => setDisclaimerText(e.target.value)}
                        placeholder="Enter your custom disclaimer text"
                        sx={{ mt: 2 }}
                        helperText={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Customize your affiliate disclosure statement
                            <Tooltip title="Write a clear disclosure that explains your relationship with Amazon and how you earn commissions">
                              <IconButton size="small" sx={{ p: 0 }}>
                                <Info size={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                      />
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Image Settings */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: BORDER_COLOR,
                  background: CARD_BACKGROUND,
                  overflow: 'hidden',
                  mb: 3
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  mb: 3,
                  position: 'relative'
                }}>
                  <Box sx={{ 
                    minWidth: 40,
                    height: 40,
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                    fontSize: '1rem',
                    fontWeight: 700
                  }}>
                    5
                  </Box>
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#1F2937',
                        position: 'relative',
                        display: 'inline-block',
                        fontSize: '1.125rem',
                        lineHeight: 1.2,
                        pb: 0.5
                      }}
                    >
                      Image Settings
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mt: 0.5, fontSize: '0.875rem' }}
                    >
                      Configure featured and product images
                    </Typography>
                  </Box>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: TEXT_PRIMARY }}>
                      Featured Image
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeFeaturedImage}
                          onChange={(e) => setIncludeFeaturedImage(e.target.checked)}
                          disabled={isGenerating}
                          color="primary"
                        />
                      }
                      label="Include Featured Image"
                    />
                    {includeFeaturedImage && (
                      <>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <ImageControls
                            model={featuredImageModel}
                            setModel={setFeaturedImageModel}
                            dimension={featuredDimension}
                            setDimension={setFeaturedDimension}
                            disabled={isGenerating}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            label="Image Prompt"
                            value={featuredImagePrompt}
                            onChange={(e) => setFeaturedImagePrompt(e.target.value)}
                            disabled={isGenerating}
                            helperText="Leave empty to use article title"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <FormControl fullWidth>
                            <InputLabel>Image Style</InputLabel>
                            <Select
                              value={featuredImageStyle}
                              onChange={(e) => setFeaturedImageStyle(e.target.value)}
                              disabled={isGenerating}
                              sx={{ borderRadius: '8px' }}
                            >
                              <MenuItem value="realistic_image">Realistic</MenuItem>
                              <MenuItem value="cartoon_image">Cartoon</MenuItem>
                              <MenuItem value="minimal_image">Minimal</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={useAIEnhancement}
                                onChange={(e) => setUseAIEnhancement(e.target.checked)}
                                disabled={isGenerating}
                                color="primary"
                              />
                            }
                            label="Use AI Prompt Enhancement"
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Generate Button - Under Right Column */}
            <Grid item xs={12}>
              <Button
                onClick={handleGenerateReview}
                variant="contained"
                size="medium"
                disabled={
                  isGenerating || 
                  isLoading || 
                  !selectedSite || 
                  !selectedCategory || 
                  !selectedPrompt || 
                  !title.trim() ||
                  !hasProducts
                }
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: '8px',
                  bgcolor: PRIMARY_COLOR,
                  '&:hover': {
                    bgcolor: alpha(PRIMARY_COLOR, 0.9)
                  },
                  '&:disabled': {
                    bgcolor: alpha(PRIMARY_COLOR, 0.4)
                  }
                }}
              >
                {isGenerating ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    <span>Generating...</span>
                  </Box>
                ) : (
                  "Generate Product Review"
                )}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Loading Overlay */}
      <LoadingOverlay visible={isGenerating} status={generationStatus} steps={generationSteps} />

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {previewPrompt?.name}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            {previewPrompt?.prompt_text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Picker Dialog */}
      <ColorPickerDialog
        open={showColorPicker}
        onClose={() => setShowColorPicker(false)}
        color={buttonColor}
        onConfirm={(newColor) => {
          setButtonColor(newColor);
          setShowColorPicker(false);
        }}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ 
            width: '100%',
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      <LeaveConfirmationDialog
    open={leaveDialogOpen}
    onClose={() => setLeaveDialogOpen(false)}
    onConfirm={() => {
      setIsGenerating(false);
      setLeaveDialogOpen(false);
      if (navigationTarget) {
        window.location.href = navigationTarget;
      }
    }}
  />
  <NavigationDialog />

      {/* Help Dialog */}
      <HelpDialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
    </Box>

);
}